import { ElLoading, ElMessage } from 'element-plus';
import { usePageState } from '@/stores/pageState';
import { usePlayerState } from '@/stores/playerState';
import { googleAuthService } from '@/services/GoogleAuthService';
import { postTokenToDriveStreamSw } from '@/services/driveStreamSwBridge';
import { alwaysLog, devLog } from '@/utils/logger';
// 注意：Record checkpoint 由 HeaderContainer 注册，避免重复实现命名/DTO；勿静态导入 ProjectService / VideoTrack 链

const RECOVERY_SNAPSHOT_KEY = 't4.mediaRecovery.snapshot';
const RECOVERY_COOLDOWN_KEY = 't4.mediaRecovery.cooldownUntil';
const COOLDOWN_MS = 60_000;
const MASK_TEXT = 'Auto-saving / Creating checkpoint...';

export type MediaRecoveryPanel = 'text' | 'record';

export interface MediaRecoverySnapshot {
  panel: MediaRecoveryPanel;
  isLongFormat: boolean;
  driveId: string;
  firstLevelFolderId: string;
  secondLevelFolderId: string;
  triggeredAt: number;
  reason: string;
}

export interface MediaRecoveryLoadContext {
  driveId: string;
  firstLevelFolderId: string;
  secondLevelFolderId: string;
  isLongFormat: boolean;
}

type LoadingInstance = { close: () => void; setText?: (text: string) => void };

let inProgress = false;
let loadingInstance: LoadingInstance | null = null;

function isRecoveryPanel(panel: string): panel is MediaRecoveryPanel {
  return panel === 'text' || panel === 'record';
}

function readCooldownUntil(): number {
  const raw = sessionStorage.getItem(RECOVERY_COOLDOWN_KEY);
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function isInCooldown(): boolean {
  return Date.now() < readCooldownUntil();
}

function setCooldown(): void {
  sessionStorage.setItem(RECOVERY_COOLDOWN_KEY, String(Date.now() + COOLDOWN_MS));
}

export function peekMediaRecoverySnapshot(): MediaRecoverySnapshot | null {
  try {
    const raw = sessionStorage.getItem(RECOVERY_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MediaRecoverySnapshot;
    if (!parsed || !isRecoveryPanel(parsed.panel)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSnapshot(snapshot: MediaRecoverySnapshot): void {
  sessionStorage.setItem(RECOVERY_SNAPSHOT_KEY, JSON.stringify(snapshot));
}

function clearSnapshot(): void {
  sessionStorage.removeItem(RECOVERY_SNAPSHOT_KEY);
}

export function showRecoveryMask(text = MASK_TEXT): void {
  if (loadingInstance) {
    loadingInstance.setText?.(text);
    return;
  }
  loadingInstance = ElLoading.service({
    fullscreen: true,
    text,
    background: 'rgba(0, 0, 0, 0.55)'
  });
}

export function closeRecoveryMask(): void {
  loadingInstance?.close();
  loadingInstance = null;
}

/** 启动时若存在未完成恢复快照，立刻盖蒙版，避免用户操作 */
export function showRecoveryMaskIfPending(): void {
  if (peekMediaRecoverySnapshot()) {
    showRecoveryMask('Restoring project / Loading latest...');
  }
}

export function isMediaRecoveryInProgress(): boolean {
  return inProgress || Boolean(peekMediaRecoverySnapshot());
}

async function ensureFreshToken(): Promise<void> {
  const ok = await googleAuthService.ensureAuthenticated();
  if (!ok) {
    throw new Error('Authentication failed while preparing media recovery');
  }
  // 主动静默刷新一次，尽量避开即将过期的 token
  await googleAuthService.refreshAccessTokenSilently();
  const token = googleAuthService.getAccessToken();
  if (token) {
    postTokenToDriveStreamSw(token);
  }
}

/** Record 面板 checkpoint 与 Submit 共用同一套上传实现（由 HeaderContainer 注册） */
type RecordProjectCommitFn = () => Promise<void>;
let commitRecordProjectHandler: RecordProjectCommitFn | null = null;

export function registerCommitRecordProject(handler: RecordProjectCommitFn | null): void {
  commitRecordProjectHandler = handler;
}

async function commitRecordProject(): Promise<void> {
  if (!commitRecordProjectHandler) {
    throw new Error('Record checkpoint commit handler is not registered');
  }
  await commitRecordProjectHandler();
}

async function commitForPanel(panel: MediaRecoveryPanel): Promise<void> {
  if (panel === 'text') {
    const { saveTextJsonToDrive } = await import('@/services/drive/textJsonDriveSave');
    const ok = await saveTextJsonToDrive();
    if (!ok) {
      throw new Error('Failed to save text JSON checkpoint to Drive');
    }
    return;
  }
  await commitRecordProject();
}

async function runPhase1(reason: string): Promise<void> {
  const pageStore = usePageState();
  const playerState = usePlayerState();
  const panel = pageStore.activePanel;

  if (!isRecoveryPanel(panel)) {
    inProgress = false;
    closeRecoveryMask();
    return;
  }

  try {
    playerState.isPause = true;
    showRecoveryMask(MASK_TEXT);

    await ensureFreshToken();
    showRecoveryMask('Auto-saving / Creating checkpoint...');
    await commitForPanel(panel);

    pageStore.setUnsavedChanges(false);

    const snapshot: MediaRecoverySnapshot = {
      panel,
      isLongFormat: Boolean(pageStore.isLongFormat),
      driveId: pageStore.selectedDriveId,
      firstLevelFolderId: pageStore.selectedFirstLevelFolderId,
      secondLevelFolderId: pageStore.selectedSecondLevelFolderId,
      triggeredAt: Date.now(),
      reason
    };
    writeSnapshot(snapshot);
    // 与 auto compose 一致：写入 session，刷新后可恢复
    pageStore.isLongFormat = snapshot.isLongFormat;
    sessionStorage.setItem('t4.isLongFormat', snapshot.isLongFormat ? '1' : '0');
    sessionStorage.setItem('t4.activePanel', panel);

    devLog.log('[MediaRecovery] Checkpoint ready, reloading...', snapshot);
    window.location.reload();
  } catch (error) {
    alwaysLog.error('[MediaRecovery] Phase1 failed:', error);
    ElMessage.error(
      `Media recovery failed: ${error instanceof Error ? error.message : 'unknown error'}`
    );
    setCooldown();
    inProgress = false;
    closeRecoveryMask();
  }
}

/**
 * 视频/长音频首次 MediaError 时触发。
 * 仅 text / record 面板生效；冷却期内或已在恢复中则忽略。
 * @returns 是否已接管（调用方应跳过本地轻量恢复）
 */
export function requestMediaRecovery(reason: string): boolean {
  if (inProgress) return true;
  if (peekMediaRecoverySnapshot()) return true;
  if (isInCooldown()) {
    devLog.warn('[MediaRecovery] In cooldown, skip escalation:', reason);
    return false;
  }

  const pageStore = usePageState();
  if (!isRecoveryPanel(pageStore.activePanel)) {
    return false;
  }

  inProgress = true;
  showRecoveryMask(MASK_TEXT);
  runPhase1(reason).catch(error => {
    alwaysLog.error('[MediaRecovery] Unhandled phase1 error:', error);
  });
  return true;
}

/**
 * 刷新后由 HeaderContainer 调用：按快照执行 Auto Compose Load-Latest，完成后关蒙版。
 */
export async function resumeMediaRecoveryIfNeeded(
  loadLatest: (panel: MediaRecoveryPanel, ctx: MediaRecoveryLoadContext) => Promise<void>
): Promise<boolean> {
  const snapshot = peekMediaRecoverySnapshot();
  if (!snapshot) return false;

  inProgress = true;
  showRecoveryMask('Restoring project / Loading latest...');

  const pageStore = usePageState();
  try {
    pageStore.activePanel = snapshot.panel;
    pageStore.isLongFormat = snapshot.isLongFormat;
    sessionStorage.setItem('t4.activePanel', snapshot.panel);
    sessionStorage.setItem('t4.isLongFormat', snapshot.isLongFormat ? '1' : '0');

    if (snapshot.driveId) {
      pageStore.selectedDriveId = snapshot.driveId;
      localStorage.setItem('t4.selectedDriveId', snapshot.driveId);
    }
    if (snapshot.firstLevelFolderId) {
      pageStore.selectedFirstLevelFolderId = snapshot.firstLevelFolderId;
      localStorage.setItem('t4.selectedFirstLevelFolderId', snapshot.firstLevelFolderId);
    }
    if (snapshot.secondLevelFolderId) {
      pageStore.selectedSecondLevelFolderId = snapshot.secondLevelFolderId;
      localStorage.setItem('t4.selectedSecondLevelFolderId', snapshot.secondLevelFolderId);
    }

    await ensureFreshToken();

    const ctx: MediaRecoveryLoadContext = {
      driveId: snapshot.driveId || pageStore.selectedDriveId,
      firstLevelFolderId: snapshot.firstLevelFolderId || pageStore.selectedFirstLevelFolderId,
      secondLevelFolderId: snapshot.secondLevelFolderId || pageStore.selectedSecondLevelFolderId,
      isLongFormat: snapshot.isLongFormat
    };

    if (!ctx.driveId || !ctx.firstLevelFolderId || !ctx.secondLevelFolderId) {
      throw new Error('Missing Drive path for Load-Latest recovery');
    }

    await loadLatest(snapshot.panel, ctx);
    clearSnapshot();
    setCooldown();
    ElMessage.success('Media recovery complete');
    return true;
  } catch (error) {
    alwaysLog.error('[MediaRecovery] Resume failed:', error);
    ElMessage.error(
      `Failed to restore after reload: ${error instanceof Error ? error.message : 'unknown error'}`
    );
    clearSnapshot();
    setCooldown();
    return false;
  } finally {
    inProgress = false;
    closeRecoveryMask();
  }
}
